import React, { useMemo } from 'react';

const SIZE = 80;
const STROKE_WIDTH = 8;
const RADIUS = (SIZE - STROKE_WIDTH) / 2;
const CENTER = SIZE / 2;

// 270-degree arc: starts at 135\u00b0, sweeps 270\u00b0
const START_ANGLE = 135;
const SWEEP = 270;

function polarToCartesian(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return {
    x: cx + r * Math.cos(rad),
    y: cy + r * Math.sin(rad),
  };
}

function describeArc(cx, cy, r, startAngle, endAngle) {
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 0 ${end.x} ${end.y}`;
}

function getThresholdColor(value, thresholds, defaultColor) {
  if (!thresholds || thresholds.length === 0) return defaultColor;

  // thresholds are sorted ascending by value; pick the color whose threshold the value is below
  for (let i = 0; i < thresholds.length; i++) {
    if (value < thresholds[i].value) {
      return thresholds[i].color;
    }
  }
  // value exceeds all thresholds \u2013 use last color
  return thresholds[thresholds.length - 1].color;
}

/**
 * Semi-circular (270\u00b0) gauge widget rendered as SVG.
 *
 * @param {{ value: number, unit: string, min: number, max: number, color: string, thresholds?: Array<{value: number, color: string}> }} props
 */
export default function GaugeWidget({
  value = 0,
  unit = '',
  min = 0,
  max = 100,
  color = '#3B82F6',
  thresholds,
}) {
  const clamped = Math.max(min, Math.min(max, value));
  const ratio = (clamped - min) / (max - min);

  const activeColor = useMemo(
    () => getThresholdColor(clamped, thresholds, color),
    [clamped, thresholds, color],
  );

  const endAngle = START_ANGLE + SWEEP * ratio;

  const bgPath = describeArc(CENTER, CENTER, RADIUS, START_ANGLE, START_ANGLE + SWEEP);
  const fgPath =
    ratio > 0
      ? describeArc(CENTER, CENTER, RADIUS, START_ANGLE, endAngle)
      : '';

  const displayValue =
    Number.isFinite(value) ? (Number.isInteger(value) ? value : value.toFixed(1)) : '--';

  return (
    <svg
      width={SIZE}
      height={SIZE}
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      className="block"
      role="img"
      aria-label={`${displayValue} ${unit}`}
    >
      {/* Background track */}
      <path
        d={bgPath}
        fill="none"
        stroke="#E5E7EB"
        strokeWidth={STROKE_WIDTH}
        strokeLinecap="round"
      />

      {/* Active arc */}
      {fgPath && (
        <path
          d={fgPath}
          fill="none"
          stroke={activeColor}
          strokeWidth={STROKE_WIDTH}
          strokeLinecap="round"
          style={{
            transition: 'stroke 0.4s ease, d 0.6s ease',
          }}
        />
      )}

      {/* Value text */}
      <text
        x={CENTER}
        y={CENTER - 2}
        textAnchor="middle"
        dominantBaseline="central"
        className="font-semibold"
        style={{ fontSize: 16, fill: '#1F2937' }}
      >
        {displayValue}
      </text>

      {/* Unit text */}
      <text
        x={CENTER}
        y={CENTER + 16}
        textAnchor="middle"
        dominantBaseline="central"
        style={{ fontSize: 10, fill: '#9CA3AF' }}
      >
        {unit}
      </text>
    </svg>
  );
}
